"use client";

import { SupportedLanguage } from "../../config/languages";
import { useBodyScrollLock } from "../../helpers/hooks/useBodyScrollLock";
import { useClickOutside } from "../../helpers/hooks/useClickOutside";
import { resolveLinkUrl } from "../../helpers/linkHelpers";
import { buildPageUrl } from "../../helpers/urlHelpers";
import { Navigation as NavigationType } from "../../payload-types";
import { Button } from "../Button/Button";
import { Link } from "../Link/Link";
import { LanguageDropdown } from "./LanguageDropdown/LanguageDropdown";
import { MobileNavMenu } from "./MobileNavMenu/MobileNavMenu";
import { NavDropdown } from "./NavDropdown/NavDropdown";
import { NavMenuItem } from "./types";
import classNames from "classnames/bind";
import NextLink from "next/link";
import { useEffect, useRef, useState } from "react";
import styles from "./Nav.module.css";

const cx = classNames.bind(styles);
const NAV_DESKTOP_MEDIA_QUERY = "(width >= 1310px)";

interface NavProps {
  currentLang: SupportedLanguage;
  data?: NavigationType;
  localizedSlugs?: Record<string, string>;
  variant?: "default" | "dark";
}

export const Nav = ({
  currentLang,
  data,
  localizedSlugs,
  variant = "default",
}: NavProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileSubmenu, setMobileSubmenu] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (!isMobileMenuOpen) {
      setMobileSubmenu(null); // Reset submenu when closing
    }
  };

  const handleDropdownClick = (e: React.MouseEvent, label: string) => {
    e.preventDefault();
    setOpenDropdown(openDropdown === label ? null : label);
  };

  const focusCurrentDropdownTrigger = (label: string) => {
    const triggerEl = navRef.current?.querySelector<HTMLElement>(
      `[data-top-nav-link="true"][data-nav-label="${label}"]`,
    );
    triggerEl?.focus();
  };

  const focusNextTopLevelLink = (label: string) => {
    if (!menuItems || menuItems.length === 0) return;

    const currentIndex = menuItems.findIndex((item) => item.label === label);
    if (currentIndex < 0) return;

    const nextItem = menuItems
      .slice(currentIndex + 1)
      .find((item) => Boolean(item.label));

    if (nextItem?.label) {
      const nextLinkEl = navRef.current?.querySelector<HTMLElement>(
        `[data-top-nav-link="true"][data-nav-label="${nextItem.label}"]`,
      );
      nextLinkEl?.focus();
      return;
    }

    const firstActionEl = navRef.current?.querySelector<HTMLElement>(
      '[class*="actions"] a[href], [class*="actions"] button',
    );
    firstActionEl?.focus();
  };

  const handleDropdownContentKeyDown = (e: React.KeyboardEvent) => {
    if (!openDropdown || !openDropdownId || e.key !== "Tab") return;

    const dropdownEl = document.getElementById(openDropdownId);
    if (!dropdownEl) return;

    const activeEl = document.activeElement as HTMLElement | null;
    if (!activeEl || !dropdownEl.contains(activeEl)) return;

    const focusableItems = Array.from(
      dropdownEl.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => !el.hasAttribute("disabled"));

    if (focusableItems.length === 0) return;

    const firstItem = focusableItems[0];
    const lastItem = focusableItems[focusableItems.length - 1];

    if (e.shiftKey && activeEl === firstItem) {
      e.preventDefault();
      focusCurrentDropdownTrigger(openDropdown);
      return;
    }

    if (!e.shiftKey && activeEl === lastItem) {
      e.preventDefault();
      focusNextTopLevelLink(openDropdown);
    }
  };

  const focusFirstDropdownItem = (dropdownId: string) => {
    const dropdownEl = document.getElementById(dropdownId);
    if (!dropdownEl) return;

    const firstFocusable = dropdownEl.querySelector<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    firstFocusable?.focus();
  };

  const handleDropdownKeyDown = (
    e: React.KeyboardEvent,
    label: string,
    dropdownId: string,
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpenDropdown(openDropdown === label ? null : label);
      return;
    }

    // Keep focus flow: trigger -> first item in open dropdown.
    if (e.key === "Tab" && !e.shiftKey && openDropdown === label) {
      e.preventDefault();
      requestAnimationFrame(() => focusFirstDropdownItem(dropdownId));
    }
  };

  const closeDropdown = () => {
    setOpenDropdown(null);
  };

  // Close dropdown when clicking outside
  useClickOutside(navRef, closeDropdown, !!openDropdown);

  // Prevent body scroll when mobile menu or desktop dropdown is open
  useBodyScrollLock(isMobileMenuOpen || !!openDropdown);

  // Close dropdown on ESC key and handle mobile menu
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (openDropdown) {
          closeDropdown();
        }
        if (isMobileMenuOpen) {
          setIsMobileMenuOpen(false);
          setMobileSubmenu(null);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openDropdown, isMobileMenuOpen]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(NAV_DESKTOP_MEDIA_QUERY);

    const syncNavMode = () => {
      if (mediaQuery.matches) {
        setIsMobileMenuOpen(false);
        setMobileSubmenu(null);
        return;
      }

      setOpenDropdown(null);
    };

    mediaQuery.addEventListener("change", syncNavMode);

    return () => {
      mediaQuery.removeEventListener("change", syncNavMode);
    };
  }, []);

  // Toggle desktop nav panel styles only after scrolling.
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 8);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const menuItems: NavMenuItem[] | undefined = data?.menuItems;
  const ctaButton = data?.ctaButton;
  const hasCta = Boolean(ctaButton?.label && resolveLinkUrl(ctaButton, "url"));
  const openDropdownItem = menuItems?.find(
    (item) => item.type === "dropdown" && item.label === openDropdown,
  );
  const openDropdownId = openDropdownItem?.label
    ? `dropdown-${openDropdownItem.label.toLowerCase().replace(/\s+/g, "-")}`
    : undefined;

  return (
    <>
      {/* Overlay */}
      <div
        className={cx("overlay")}
        data-open={!!openDropdown}
        onClick={closeDropdown}
      />

      <nav
        className={cx("nav")}
        ref={navRef}
        data-scrolled={isScrolled}
        data-variant={variant}
        data-has-cta={hasCta}
        data-mobile-open={isMobileMenuOpen}
      >
        <div className={cx("nav-container")}>
          {/* Logo */}
          <div className={cx("logo")}>
            <NextLink
              href={buildPageUrl("/", currentLang)}
              className={cx("logo-link")}
            >
              <svg
                viewBox="0 0 116 26"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="currentColor"
                  d="M24.267 13.367c-.19 6.416-5.354 11.578-11.77 11.767.192-6.365 5.342-11.498 11.77-11.767ZM11.676 25.132C5.299 24.895.183 19.742 0 13.353h11.676v11.779Zm40.686-20.03H40.134v6.119h10.655v3.037H40.134v6.312h12.448v3.037H36.278V2.064h16.084v3.038Zm39.358 5.923h-2.251c-2.77 0-3.982 1.39-3.982 4.309v8.273h-3.73V8.152h3.571v1.78h.253c.582-1.121 1.653-1.84 3.872-1.84h2.267v2.933Zm15.342 2.348h.25l4.36-5.22h3.777v.449l-5.995 7.18 5.729 7.376v.45h-3.84l-4.219-5.431h-.25l-4.532 5.43h-3.793v-.449l6.184-7.405-5.57-7.151v-.45h3.855l4.044 5.221Zm-43.56-5.55c3.84 0 6.201 1.87 6.201 5.67v10.112h-3.73v-9.8c0-2.139-1.212-3.14-3.415-3.14-2.408 0-4.313 1.301-4.313 4.293v8.647h-3.73V8.152h3.606v1.705h.251c1.117-1.45 2.928-2.034 5.13-2.034Zm11.867.33h4.595v2.737h-4.595v9.545l.253.238h4.266v2.933h-3.763c-2.817 0-4.484-1.378-4.484-4.01V8.152h-.002v-4.1h3.73v4.1Zm21.606 15.452h-3.73V8.152h3.73v15.454ZM11.772.867C11.578 7.232 6.429 12.367 0 12.636.19 6.219 5.354 1.056 11.771.867Zm.714 0c6.39.183 11.542 5.299 11.779 11.676H12.486V.867ZM97.101 5.85h-3.998V2.588h3.998V5.85Z"
                />
              </svg>
            </NextLink>
          </div>

          {/* Desktop Navigation */}
          <div className={cx("desktop")}>
            <ul className={cx("list")}>
              {menuItems?.map((item, index) => {
                if (!item.label) return null;
                const dropdownId = `dropdown-${item.label.toLowerCase().replace(/\s+/g, "-")}`;

                return (
                  <li key={item.label || index} className={cx("item")}>
                    {item.type === "dropdown" ? (
                      <button
                        className={cx("link", "dropdown-trigger")}
                        onClick={(e) => handleDropdownClick(e, item.label)}
                        onKeyDown={(e) =>
                          handleDropdownKeyDown(e, item.label, dropdownId)
                        }
                        data-dropdown={true}
                        data-top-nav-link="true"
                        data-nav-label={item.label}
                        data-open={openDropdown === item.label}
                        aria-expanded={openDropdown === item.label}
                        aria-haspopup="true"
                        aria-controls={dropdownId}
                      >
                        {item.label}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M10.0005 10.9763L14.1254 6.8515L15.3039 8.03001L10.0005 13.3334L4.69727 8.03001L5.87577 6.8515L10.0005 10.9763Z"
                            fill="currentColor"
                          />
                        </svg>
                      </button>
                    ) : (
                      <Link
                        href={resolveLinkUrl(item.linkSettings, "url") || ""}
                        className={cx("link")}
                        data-dropdown={false}
                        data-top-nav-link="true"
                        data-nav-label={item.label}
                        openInNewTab={item.linkSettings?.openInNewTab || false}
                        locale={currentLang}
                      >
                        {item.label}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
            <NavDropdown
              columns={openDropdownItem?.dropdownColumns || undefined}
              isOpen={!!openDropdownItem}
              currentLang={currentLang}
              variant={variant}
              isScrolled={isScrolled}
              dropdownId={openDropdownId}
              onClick={closeDropdown}
              onDropdownKeyDown={handleDropdownContentKeyDown}
            />
          </div>

          {/* Desktop Actions */}
          <div className={cx("actions")}>
            <LanguageDropdown
              currentLang={currentLang}
              localizedSlugs={localizedSlugs}
              variant={variant}
              isScrolled={isScrolled}
              isMobileMenuOpen={isMobileMenuOpen}
            />
            {ctaButton &&
              ctaButton.label &&
              resolveLinkUrl(ctaButton, "url") && (
                <Button
                  className={cx("desktop-cta")}
                  variant="primary"
                  size="small"
                  href={resolveLinkUrl(ctaButton, "url")!}
                  locale={currentLang}
                >
                  {ctaButton.label}
                </Button>
              )}
          </div>

          {/* Mobile Actions */}
          <div className={cx("mobile-actions")}>
            <LanguageDropdown
              currentLang={currentLang}
              localizedSlugs={localizedSlugs}
              variant={variant}
              isScrolled={isScrolled}
              isMobileMenuOpen={isMobileMenuOpen}
            />
            <button
              className={cx("mobile-toggle")}
              aria-expanded={isMobileMenuOpen}
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className={cx("mobile-grid-icon")}
                  aria-hidden="true"
                >
                  <path
                    d="M5 6.5C5.82843 6.5 6.5 5.82843 6.5 5C6.5 4.17157 5.82843 3.5 5 3.5C4.17157 3.5 3.5 4.17157 3.5 5C3.5 5.82843 4.17157 6.5 5 6.5Z"
                    fill="white"
                  />
                  <path
                    d="M10 6.5C10.8284 6.5 11.5 5.82843 11.5 5C11.5 4.17157 10.8284 3.5 10 3.5C9.17157 3.5 8.5 4.17157 8.5 5C8.5 5.82843 9.17157 6.5 10 6.5Z"
                    fill="#131941"
                  />
                  <path
                    d="M15 6.5C15.8284 6.5 16.5 5.82843 16.5 5C16.5 4.17157 15.8284 3.5 15 3.5C14.1716 3.5 13.5 4.17157 13.5 5C13.5 5.82843 14.1716 6.5 15 6.5Z"
                    fill="white"
                  />
                  <path
                    d="M5 11.5C5.82843 11.5 6.5 10.8284 6.5 10C6.5 9.17157 5.82843 8.5 5 8.5C4.17157 8.5 3.5 9.17157 3.5 10C3.5 10.8284 4.17157 11.5 5 11.5Z"
                    fill="#131941"
                  />
                  <path
                    d="M10 11.5C10.8284 11.5 11.5 10.8284 11.5 10C11.5 9.17157 10.8284 8.5 10 8.5C9.17157 8.5 8.5 9.17157 8.5 10C8.5 10.8284 9.17157 11.5 10 11.5Z"
                    fill="white"
                  />
                  <path
                    d="M15 11.5C15.8284 11.5 16.5 10.8284 16.5 10C16.5 9.17157 15.8284 8.5 15 8.5C14.1716 8.5 13.5 9.17157 13.5 10C13.5 10.8284 14.1716 11.5 15 11.5Z"
                    fill="#131941"
                  />
                  <path
                    d="M5 16.5C5.82843 16.5 6.5 15.8284 6.5 15C6.5 14.1716 5.82843 13.5 5 13.5C4.17157 13.5 3.5 14.1716 3.5 15C3.5 15.8284 4.17157 16.5 5 16.5Z"
                    fill="white"
                  />
                  <path
                    d="M10 16.5C10.8284 16.5 11.5 15.8284 11.5 15C11.5 14.1716 10.8284 13.5 10 13.5C9.17157 13.5 8.5 14.1716 8.5 15C8.5 15.8284 9.17157 16.5 10 16.5Z"
                    fill="#131941"
                  />
                  <path
                    d="M15 13.5C15.83 13.5 16.5 14.17 16.5 15C16.5 15.83 15.83 16.5 15 16.5C14.17 16.5 13.5 15.83 13.5 15C13.5 14.17 14.17 13.5 15 13.5Z"
                    fill="white"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className={cx("mobile-grid-icon")}
                  aria-hidden="true"
                >
                  <path
                    d="M5 6.5C5.82843 6.5 6.5 5.82843 6.5 5C6.5 4.17157 5.82843 3.5 5 3.5C4.17157 3.5 3.5 4.17157 3.5 5C3.5 5.82843 4.17157 6.5 5 6.5Z"
                    fill="#16265B"
                  />
                  <path
                    d="M10 6.5C10.8284 6.5 11.5 5.82843 11.5 5C11.5 4.17157 10.8284 3.5 10 3.5C9.17157 3.5 8.5 4.17157 8.5 5C8.5 5.82843 9.17157 6.5 10 6.5Z"
                    fill="#16265B"
                  />
                  <path
                    d="M15 6.5C15.8284 6.5 16.5 5.82843 16.5 5C16.5 4.17157 15.8284 3.5 15 3.5C14.1716 3.5 13.5 4.17157 13.5 5C13.5 5.82843 14.1716 6.5 15 6.5Z"
                    fill="#16265B"
                  />
                  <path
                    d="M5 11.5C5.82843 11.5 6.5 10.8284 6.5 10C6.5 9.17157 5.82843 8.5 5 8.5C4.17157 8.5 3.5 9.17157 3.5 10C3.5 10.8284 4.17157 11.5 5 11.5Z"
                    fill="#16265B"
                  />
                  <path
                    d="M10 11.5C10.8284 11.5 11.5 10.8284 11.5 10C11.5 9.17157 10.8284 8.5 10 8.5C9.17157 8.5 8.5 9.17157 8.5 10C8.5 10.8284 9.17157 11.5 10 11.5Z"
                    fill="#16265B"
                  />
                  <path
                    d="M15 11.5C15.8284 11.5 16.5 10.8284 16.5 10C16.5 9.17157 15.8284 8.5 15 8.5C14.1716 8.5 13.5 9.17157 13.5 10C13.5 10.8284 14.1716 11.5 15 11.5Z"
                    fill="#16265B"
                  />
                  <path
                    d="M5 16.5C5.82843 16.5 6.5 15.8284 6.5 15C6.5 14.1716 5.82843 13.5 5 13.5C4.17157 13.5 3.5 14.1716 3.5 15C3.5 15.8284 4.17157 16.5 5 16.5Z"
                    fill="#16265B"
                  />
                  <path
                    d="M10 16.5C10.8284 16.5 11.5 15.8284 11.5 15C11.5 14.1716 10.8284 13.5 10 13.5C9.17157 13.5 8.5 14.1716 8.5 15C8.5 15.8284 9.17157 16.5 10 16.5Z"
                    fill="#16265B"
                  />
                  <path
                    d="M15 13.5C15.83 13.5 16.5 14.17 16.5 15C16.5 15.83 15.83 16.5 15 16.5C14.17 16.5 13.5 15.83 13.5 15C13.5 14.17 14.17 13.5 15 13.5Z"
                    fill="#16265B"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        <MobileNavMenu
          isOpen={isMobileMenuOpen}
          items={menuItems}
          ctaButton={ctaButton}
          submenu={mobileSubmenu}
          onClose={() => setIsMobileMenuOpen(false)}
          onSubmenuChange={setMobileSubmenu}
          locale={currentLang}
        />
      </nav>
    </>
  );
};
